import { isEmpty } from 'lodash';
import { ReactElement } from 'react';

import {
   MedicalInformationCountByType,
   MedicalInformationCountByTypeQuery,
} from '@dreryk/egabinet-patient-medical-information-frontend-api';

import { useCqrsSearch } from '@dreryk/command-query-react-common';
import { EmptyStatePlaceholder, LoaderSpinner, Message, MessageType } from '@dreryk/react-components';

import { patientMedicalInformationTranslationKeys } from '../translations';

import { useShowRemovedMedicalInformationState } from './MedicalInformationShowRemovedCheckbox.hooks';

type MedicalInformationAccordionLoaderRenderer = (renderProps: {
   medicalInformationCountByTypes: MedicalInformationCountByType[];
}) => ReactElement;

type MedicalInformationAccordionLoaderProps = {
   readonly patientId: number;
   readonly children: MedicalInformationAccordionLoaderRenderer;
};

export const MedicalInformationAccordionLoader = (props: MedicalInformationAccordionLoaderProps): JSX.Element => {
   const [showRemoved] = useShowRemovedMedicalInformationState();

   const {
      data: medicalInformationCountByType,
      loading: medicalInformationCountByTypeLoading,
      error: medicalInformationCountByTypeError,
   } = useCqrsSearch<MedicalInformationCountByType>(() => {
      const query = new MedicalInformationCountByTypeQuery();
      query.patientId = props.patientId;
      query.showRemoved = showRemoved;
      return query;
   });

   if (medicalInformationCountByTypeLoading) {
      return <LoaderSpinner />;
   }

   if (medicalInformationCountByTypeError) {
      return (
         <Message
            testId={'failedToFetchMedicalInformation'}
            label={patientMedicalInformationTranslationKeys.failedToFetchMedicalInformation}
            type={MessageType.NEGATIVE}
            displayIndicator
         />
      );
   }

   if (isEmpty(medicalInformationCountByType)) {
      return (
         <EmptyStatePlaceholder
            heading={patientMedicalInformationTranslationKeys.noEntries}
            subheading={patientMedicalInformationTranslationKeys.patientHasNoEntries}
            testId={'noEntries'}
         />
      );
   }

   return props.children({
      medicalInformationCountByTypes: medicalInformationCountByType,
   });
};
